/**
 * data-cache.ts
 *
 * A lightweight in-memory key/value cache with per-entry TTL support.
 *
 * Intended for caching API responses so that repeated navigations to the same
 * page surface data immediately (stale-while-revalidate) without waiting for
 * another round-trip.  The cache lives in module scope, so it persists for the
 * lifetime of the JS bundle (i.e. a single browser session / SPA lifecycle).
 *
 * Cache keys are plain strings.  Consumers should use the exported constants
 * (e.g. OFFICES_CACHE_KEY) to avoid magic-string coupling.
 *
 * Usage example:
 *   // Read
 *   const cached = dataCache.get<Office[]>(OFFICES_CACHE_KEY);
 *
 *   // Write
 *   dataCache.set(OFFICES_CACHE_KEY, offices);
 *
 *   // Invalidate (e.g. after a mutation)
 *   dataCache.invalidate(OFFICES_CACHE_KEY);
 */

/** Default cache lifetime in milliseconds (5 minutes). */
const DEFAULT_TTL_MS = 5 * 60 * 1_000;

interface CacheEntry<T> {
  readonly data: T;
  readonly expiresAt: number;
}

class DataCache {
  private readonly store = new Map<string, CacheEntry<unknown>>();

  /**
   * Returns the cached value for `key`, or `undefined` if the entry does not
   * exist or has expired.  Expired entries are evicted on access.
   */
  get<T>(key: string): T | undefined {
    const entry = this.store.get(key);
    if (entry === undefined) return undefined;

    if (Date.now() > entry.expiresAt) {
      this.store.delete(key);
      return undefined;
    }

    return entry.data as T;
  }

  /**
   * Stores `data` under `key` with the given TTL (defaults to 5 minutes).
   */
  set<T>(key: string, data: T, ttlMs: number = DEFAULT_TTL_MS): void {
    this.store.set(key, { data, expiresAt: Date.now() + ttlMs });
  }

  /**
   * Removes the entry for `key` immediately, forcing the next `get` to return
   * `undefined` even if the TTL has not yet elapsed.
   *
   * Call this whenever a mutation (create / update / delete) might have made
   * the cached value stale.
   */
  invalidate(key: string): void {
    this.store.delete(key);
  }

  /** Removes ALL entries – useful for sign-out scenarios. */
  clear(): void {
    this.store.clear();
  }
}

/** Singleton cache instance shared across the entire application. */
export const dataCache = new DataCache();

// ---------------------------------------------------------------------------
// Cache key constants
// ---------------------------------------------------------------------------

/** Cache key for the admin/author offices list (`office/by-admin`). */
export const OFFICES_CACHE_KEY = 'adminOrAuthorOffices';

/** Cache key for the parkings-by-author list (`parking/parkings-by-author`). */
export const PARKINGS_CACHE_KEY = 'parkingsByAuthor';

/** Cache key for the facilities-by-user list (`facility`). */
export const FACILITIES_CACHE_KEY = 'facilitiesByUser';

/**
 * Cache key for the office-manager/admin offices list used by the
 * "Check in employees" page (`office/by-officemanager-or-admin`).
 * Per-session stale-while-revalidate so the page renders instantly on
 * subsequent visits while always fetching a fresh copy in the background.
 */
export const OFFICES_MANAGER_CACHE_KEY = 'officeManagerOrAdminOffices';

/** Cache key for the super-admin companies list (`company`). */
export const COMPANIES_CACHE_KEY = 'companies';
